import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

interface ToastNoticeProps {
  message: string;
  tone?: ToastTone;
  durationMs?: number;
  onClose: () => void;
}

export function ToastNotice({ message, tone = 'info', durationMs, onClose }: ToastNoticeProps) {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!message) return undefined;
    const timer = window.setTimeout(() => onCloseRef.current(), durationMs ?? (tone === 'error' ? 4800 : 3000));
    return () => window.clearTimeout(timer);
  }, [durationMs, message, tone]);

  if (!message) return null;
  const Icon = toastIcon(tone);

  return createPortal(
    <div className="toast-viewport" role="status" aria-live={tone === 'error' ? 'assertive' : 'polite'}>
      <div className={`app-toast toast-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
        <Icon size={18} aria-hidden="true" />
        <span>{message}</span>
        <button type="button" aria-label="关闭提示" onClick={onClose}>
          <X size={15} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

function toastIcon(tone: ToastTone) {
  if (tone === 'success') return CheckCircle2;
  if (tone === 'error') return XCircle;
  if (tone === 'warning') return AlertTriangle;
  return Info;
}
